import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components";
import { AppRoutes, LocalStorageItems } from "../../../constants";
import useApp from "../../../hooks/useApp";
import { ConfirmationModal, DetailsModal, ThanksModal } from "../../../modals";
import { IUserInputs } from "../../../modals/details-modal/const";
import {
  getCampaignById_V2,
  getNgosByCampaignAndCity_V2,
} from "../../../Services_V2/Campaign.service";
import {
  DonationPayload,
  submitDonation_V2,
  SubmitDonationFullPayload,
} from "../../../Services_V2/Donation.service";
import { getOrganizationById_V2 } from "../../../Services_V2/Organization.service";
import { INGO_V2 } from "../../../types";
import { IOrganization } from "../../../types/auth";
import { getNumber } from "../../../utils";
import { showError } from "../../../utils/error";
import indianNumberFormatter from "../../../utils/indian-number-formatter";
import { getLocalStorageItem } from "../../../utils/localStorage";
import { ICampaign } from "../../CampaignPage/const";
import { calculateTotalPledgedAmount, CampaignQueryParam } from "../const";
import NGOCard_V2 from "../ngo-card-v2/NGOCard_V2";
import NgoDetailsModal_V2 from "../ngo-details-v2/NgoDetailsModal_V2";
import OrgCard from "../org-card-v2/OrgCard";
import EmailMemberModal from "../pick-city-page/EmailMemberModal/EmailMemberModal";
import styles from "./Campaign-ngo.module.css";

const CampaignNgoPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const campaignId = searchParams.get(CampaignQueryParam.CAMPAIGN_ID);
  const cityId = searchParams.get(CampaignQueryParam.CITY);
  const email = searchParams.get(CampaignQueryParam.EMAIL);

  const {
    state: { organization, isVoting },
  } = useApp();
  const [open, setOpen] = useState(false);
  const [openEmailModel, setOpenEmailModel] = useState(false);
  const [selectedNGOS, setSelectedNGOS] = useState<
    {
      _id: string;
      value: string;
      projects?: { _id: string; value: string }[];
    }[]
  >([]);
  const [selectedOrg, setSelectedOrg] = useState<
    { _id: string; value: string }[]
  >([]);
  const [openThanksModal, setOpenThanksModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [closeProjectModal, setCloseProjectModal] = useState(false);
  const [ngos, setNGOS] = useState<INGO_V2[]>([]);
  const [campaignDetails, setCampaignDetails] = useState<ICampaign | null>(
    null,
  );
  const [orgDetails, setOrgDetails] = useState<IOrganization | null>(null);
  const [selectedNGOId, setSelectedNGOId] = useState<string | null>(null);
  const [errors, setErrors] = useState<any>({});
  const [finalSelectedNGOS, setFinalSelectedNGOS] = useState<DonationPayload[]>(
    [],
  );
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [lastSubmittedUserDetails, setLastSubmittedUserDetails] = useState<
    IUserInputs | undefined
  >(undefined);
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!campaignId) {
      navigate(AppRoutes.DONOR.DONOR_LOGIN, { replace: true });
      return;
    }
  }, [campaignId, navigate]);

  useEffect(() => {
    const storedMember = getLocalStorageItem(LocalStorageItems.MEMBER_DETAILS);

    if ((!email || email.trim() === "" || email === "null") && !storedMember) {
      setOpenEmailModel(true);
    }
  }, [email]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, _id: string) => {
      const { checked } = event?.target;

      if (
        organization?.isVoting &&
        selectedNGOS.length >= (organization?.votingLimit ?? 0) &&
        checked
      ) {
        return toast.error(
          `You can vote only ${organization?.votingLimit} ngos`,
        );
      }

      if (checked) {
        setSelectedNGOS((prev) => [...prev, { _id, value: "", projects: [] }]);
      } else {
        setSelectedNGOS((prev) => prev.filter((ngo) => ngo?._id !== _id));

        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors[_id];
          return newErrors;
        });
      }
    },
    [organization, selectedNGOS],
  );

  const handleProjectToggle = useCallback(
    (ngoId: string, projectId: string, checked: boolean) => {
      setSelectedNGOS((prev) =>
        prev.map((ngo) => {
          if (ngo._id !== ngoId) return ngo;

          const projects = ngo.projects || [];

          if (checked) {
            return {
              ...ngo,
              projects: [...projects, { _id: projectId, value: "" }],
            };
          } else {
            return {
              ...ngo,
              projects: projects.filter((p) => p._id !== projectId),
            };
          }
        }),
      );
    },
    [],
  );

  const fetchCampaign = useCallback(async () => {
    try {
      const res = await getCampaignById_V2(campaignId ?? "null");
      setCampaignDetails(res?.data || []);
    } catch (error) {
      showError(error);
    }
  }, [campaignId]);

  const fetchNGOS = useCallback(async () => {
    try {
      const res = await getNgosByCampaignAndCity_V2(
        campaignId ?? "null",
        cityId ?? "null",
      );

      setNGOS(res?.data || []);
    } catch (error) {
      showError(error);
    }
  }, [cityId]);

  const fetchOrganizationByCampaign = useCallback(async () => {
    if (!campaignDetails?.parentId) return;
    try {
      const res = await getOrganizationById_V2(
        String(campaignDetails?.parentId),
      );
      if (res?.success) {
        setOrgDetails(res?.data);
      }
    } catch (error) {}
  }, [campaignDetails?.parentId]);

  useEffect(() => {
    // if (type === ParamTypes.ORGANIZATION) {
    fetchNGOS();
    fetchCampaign();
    // }
  }, [fetchNGOS, fetchCampaign]);

  useEffect(() => {
    if (
      campaignDetails?.includeOrgForDonation === true &&
      campaignDetails?.parentId
    ) {
      fetchOrganizationByCampaign();
    } else {
      setOrgDetails(null);
    }
  }, [
    campaignDetails?.includeOrgForDonation,
    campaignDetails?.parentId,
    fetchOrganizationByCampaign,
  ]);

  const handleSubmit = useCallback(() => {
    const newErrors: Record<string, string> = { ...errors };

    selectedNGOS.forEach((ngo) => {
      const selectedProjects = ngo.projects || [];

      // If NGO selected but nothing pledged → error
      if (selectedProjects.length === 0) {
        newErrors[ngo._id] =
          "Please select at least one project or donate to NGO directly";
      }

      selectedProjects.forEach((proj) => {
        const amt = getNumber(proj.value);
        if (amt <= 0) {
          newErrors[`${ngo._id}-${proj._id}`] = "Amount must be greater than 0";
        }
      });
    });

    selectedOrg.forEach((org) => {
      if (getNumber(org.value) <= 0) {
        newErrors[org._id] = "Amount must be greater than 0";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please correct the errors before continuing");
      return;
    }

    const ngoDonations: DonationPayload[] = selectedNGOS.map((ngo) => {
      const hasGeneralDonation =
        ngo.projects?.some((p) => p._id === "general") ?? false;

      const projects = (ngo.projects || [])
        .filter((p) => p._id !== "general")
        .map((p) => ({
          projectId: p._id,
          amount: getNumber(p.value),
        }));

      const generalAmount = hasGeneralDonation
        ? getNumber(
            ngo.projects?.find((p) => p._id === "general")?.value || "0",
          )
        : 0;

      return {
        ngoId: ngo._id,
        cityId: cityId as string,
        campaignId: campaignId as string,
        isDonationForNgo: hasGeneralDonation,
        ...(hasGeneralDonation && { amount: generalAmount }),
        projects: projects.length > 0 ? projects : undefined,
      };
    });

    // ── Organization donations ──//
    const orgDonations: DonationPayload[] = selectedOrg.map((org) => ({
      organisationId: org._id,
      amount: getNumber(org.value),
      cityId: cityId as string,
      campaignId: campaignId as string,
    }));

    const finalPayload = [...ngoDonations, ...orgDonations];

    setFinalSelectedNGOS(finalPayload);
    setOpenDetailsModal(true);
  }, [selectedNGOS, selectedOrg, cityId, campaignId, errors]);

  const handleDonation = useCallback(
    async (values?: IUserInputs, force: boolean = false) => {
      if (!values) {
        toast.error("User details are missing.");
        return;
      }

      try {
        setCloseProjectModal(true);

        const fullPayload: SubmitDonationFullPayload = {
          donor: [
            {
              name: values.name?.trim() || "",
              email: values.email?.trim() || "",
              phone: values.phone?.toString().trim() || "",
              userType: values.userType || "Individual",

              ...(values.userType === "Corporate" &&
                values.position?.trim() && {
                  position: values.position.trim(),
                }),

              ...(values.pan_number?.trim() && {
                pan_number: values.pan_number.trim(),
              }),
            },
          ],

          donations: finalSelectedNGOS,
          force,
        };

        const res = await submitDonation_V2(fullPayload, force);

        toast.success(res?.message || "Donation successful!");
        setOpenDetailsModal(false);
        setCloseProjectModal(false);
        setOpenThanksModal(true);

        setSelectedNGOS([]);
        setSelectedOrg([]);
        setFinalSelectedNGOS([]);
        setErrors({});
        setSelectedNGOId(null);
        await fetchNGOS();
      } catch (error: any) {
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.code === "DUPLICATE_DONATION"
        ) {
          setDuplicateMessage(error.response.data.message);
          setOpenDuplicateModal(true);
          return;
        }
        showError(error);
      }
    },
    [finalSelectedNGOS, fetchNGOS],
  );

  const handleOrgCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, _id: string) => {
      const { checked } = event.target;

      if (checked) {
        setSelectedOrg((prev) => [...prev, { _id, value: "" }]);
      } else {
        setSelectedOrg((prev) => prev.filter((org) => org._id !== _id));
        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors[_id];
          return newErrors;
        });
      }
    },
    [],
  );

  const handleOrgValueChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      _id: string,
    ) => {
      const { value } = event.target;
      const formattedValue = indianNumberFormatter(String(value));
      if (formattedValue === null) return;

      const numValue = getNumber(formattedValue);

      setSelectedOrg((prev) =>
        prev.map((org) =>
          org._id === _id ? { ...org, value: formattedValue } : org,
        ),
      );

      if (numValue <= 0) {
        setErrors((prev: any) => ({
          ...prev,
          [_id]: "Value must be more than 0",
        }));
      } else {
        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors[_id];
          return newErrors;
        });
      }
    },
    [],
  );

  const cityNameTitle: string = useMemo(() => {
    const city = campaignDetails?.cities?.find((i) => i._id === cityId);
    return typeof city?.city === "string" ? city.city : "";
  }, [campaignDetails, cityId]);

  const handleProjectValueChange = useCallback(
    (ngoId: string, projectId: string, newValue: string) => {
      const digitsOnly = newValue.replace(/[^0-9]/g, "");
      const formatted = indianNumberFormatter(digitsOnly) || "";

      setSelectedNGOS((prev) =>
        prev.map((ngo) =>
          ngo._id === ngoId
            ? {
                ...ngo,
                projects: (ngo.projects || []).map((p) =>
                  p._id === projectId ? { ...p, value: formatted } : p,
                ),
              }
            : ngo,
        ),
      );

      if (formatted && getNumber(formatted) > 0) {
        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors[`${ngoId}-${projectId}`];
          return newErrors;
        });
      }
    },
    [],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headingContainer}>
          {<h2>{campaignDetails?.name}</h2>}
          <hr className={styles.centerLine} />
        </div>
        <div className={styles.headerDiscription}>
          {<h2>{campaignDetails?.description}</h2>}
          <hr className={styles.centerLine} />
        </div>

        <span
          style={{
            fontSize: "16px",
            color: " rgb(246, 209, 140)",
            fontWeight: 600,
          }}
        >
          City:{" "}
          <span
            style={{
              fontSize: "20px",
              color: " rgb(246, 209, 140)",
              fontWeight: 600,
            }}
          >
            {cityNameTitle || ""}
          </span>
        </span>

        <div className={styles.descriptions}>
          Campaign Goal:
          {
            <h2 style={{ fontSize: "20px" }}>
              ₹{indianNumberFormatter(String(campaignDetails?.goal))}
            </h2>
          }
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.cardWrapper}>
          {ngos.map((ngo) => {
            return (
              <NGOCard_V2
                key={ngo?._id}
                ngo={ngo}
                selectedNGOS={selectedNGOS}
                onCheckboxChange={handleCheckboxChange}
                onViewMoreClick={() => {
                  setOpen(true);
                  setSelectedNGOId(ngo._id);
                }}
                // onValueChange={handleChangeValue}
                isVoting={isVoting}
                errors={errors}
                onProjectToggle={handleProjectToggle}
                organization={orgDetails ?? null}
                closeProjectModel={closeProjectModal}
                onProjectValueChange={handleProjectValueChange}
                isForSingleNGO={false}
              />
            );
          })}
          {orgDetails && (
            <OrgCard
              organization={orgDetails}
              selectedOrg={selectedOrg}
              onCheckboxChange={handleOrgCheckboxChange}
              onValueChange={handleOrgValueChange}
              errors={errors}
            />
          )}
        </div>
      </div>
      {(selectedNGOS.length > 0 || selectedOrg.length > 0) && (
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSubmit}
        >
          Continue
        </Button>
      )}
      <NgoDetailsModal_V2
        open={open && !!selectedNGOId}
        onClose={() => {
          setOpen(false);
          setSelectedNGOId(null);
        }}
        ngoId={selectedNGOId}
      />
      <DetailsModal
        open={openDetailsModal}
        onClose={() => {
          setOpenDetailsModal(false);
        }}
        onSubmit={(values) => {
          setLastSubmittedUserDetails(values);
          handleDonation(values);
        }}
        isPanRequired={
          calculateTotalPledgedAmount(selectedNGOS, selectedOrg) >= 50000
        }
        email={String(email)}
      />
      <ThanksModal
        open={openThanksModal}
        onClose={() => setOpenThanksModal(false)}
      />

      <ConfirmationModal
        open={openDuplicateModal}
        onClose={() => setOpenDuplicateModal(false)}
        title="Duplicate Donation"
        message={
          duplicateMessage ||
          "You have already pledged donation for this NGO. Do you want to pledge again?"
        }
        onConfirm={() => {
          setOpenDuplicateModal(false);
          handleDonation(lastSubmittedUserDetails, true);
        }}
        confirmText="Update"
        cancelText="Cancel"
      />
      <EmailMemberModal
        open={openEmailModel}
        onClose={() => setOpenEmailModel(false)}
        initialEmail="test@example.com"
      />
    </div>
  );
};

export default CampaignNgoPage;
